/* ==========================================================================
   Meridian — storefront behaviour (no dependencies)
   Custom elements: sticky-header, announcement-bar, hero-slideshow, nav-drawer,
   filter-drawer, cart-drawer, predictive-search, product-info, product-gallery,
   product-form, sticky-atc, carousel-slider, collection-filters, quick-add-modal,
   product-recommendations. Accordions use [data-collapsible-trigger] delegation.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const mqSmall = window.matchMedia('(max-width: 768px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Utilities ---------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), wait); };
  };
  const announce = (msg) => {
    const el = document.getElementById('A11yStatus');
    if (!el) return;
    el.textContent = '';
    window.setTimeout(() => { el.textContent = msg; }, 50);
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  function formatMoney(cents, format = theme.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands = ',', dec = '.') => {
      const parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimals ? dec : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        default: return fmt(value, 2);
      }
    });
  }

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusables = (root) => $$(FOCUSABLE, root).filter((el) => el.offsetParent !== null || el === document.activeElement);

  /* ---------- Scroll lock + overlay (shared by drawers, search, modals) ---------- */
  const ScrollLock = {
    count: 0,
    lock() {
      if (this.count++ > 0) return;
      const sb = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = sb > 0 ? `${sb}px` : '';
      document.documentElement.classList.add('lock-scroll');
    },
    unlock() {
      this.count = Math.max(0, this.count - 1);
      if (this.count > 0) return;
      document.documentElement.classList.remove('lock-scroll');
      document.body.style.paddingRight = '';
    }
  };
  const Overlay = {
    get el() { return document.querySelector('[data-page-overlay]'); },
    owner: null,
    show(owner, variant) {
      const el = this.el; if (!el) return;
      this.owner = owner;
      el.classList.toggle('is-cart', variant === 'cart');
      el.classList.add('is-visible');
    },
    hide(owner) {
      if (owner && this.owner !== owner) return;
      const el = this.el; if (!el) return;
      el.classList.remove('is-visible');
      this.owner = null;
    }
  };
  document.addEventListener('click', (e) => {
    if (e.target.matches('[data-page-overlay]') && Overlay.owner && typeof Overlay.owner.close === 'function') {
      Overlay.owner.close();
    }
  });

  /* ---------- Focus trap ---------- */
  function trapFocus(container, onEscape) {
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onEscape(); return; }
      if (e.key !== 'Tab') return;
      const items = focusables(container);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }

  /* ---------- Collapsible (accordions, footer on mobile, nav sublists, filter groups) ---------- */
  function setCollapsible(trigger, open, animate = true) {
    const target = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!target) return;
    trigger.setAttribute('aria-expanded', String(open));
    const done = () => { if (target.classList.contains('is-open')) target.style.height = 'auto'; };
    if (!animate || reducedMotion.matches) {
      target.classList.toggle('is-open', open);
      target.style.height = open ? 'auto' : '0px';
      return;
    }
    if (open) {
      target.classList.add('is-open');
      target.style.height = `${target.scrollHeight}px`;
      target.addEventListener('transitionend', done, { once: true });
    } else {
      target.style.height = `${target.scrollHeight}px`;
      // force reflow so the transition runs from the measured height
      void target.offsetHeight;
      target.classList.remove('is-open');
      target.style.height = '0px';
    }
  }
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-collapsible-trigger]');
    if (!trigger) return;
    e.preventDefault();
    setCollapsible(trigger, trigger.getAttribute('aria-expanded') !== 'true');
  });
  function initCollapsibles(root = document) {
    $$('[data-open] [data-collapsible-trigger]', root).forEach((t) => {
      if (t.closest('[data-open]') && t.getAttribute('aria-expanded') !== 'true') setCollapsible(t, true, false);
    });
  }

  /* ==========================================================================
     Drawers (nav: left 350px · filters: left · cart: right 440px)
     ========================================================================== */
  class BaseDrawer extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.opener = null;
      this.releaseTrap = null;
      this.overlayVariant = 'modal';
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); this.close(); }
      });
    }
    open(opener) {
      if (this.isOpen) return;
      BaseDrawer.closeAll(this);
      this.opener = opener || document.activeElement;
      this.isOpen = true;
      this.hidden = false;
      // next frame so the transform transition runs
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      $$(`[aria-controls="${this.id}"][aria-expanded]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      ScrollLock.lock();
      Overlay.show(this, this.overlayVariant);
      this.releaseTrap = trapFocus(this, () => this.close());
      window.setTimeout(() => {
        const first = this.querySelector('[data-drawer-close]') || focusables(this)[0];
        if (first) first.focus({ preventScroll: true });
      }, 60);
    }
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      $$(`[aria-controls="${this.id}"][aria-expanded]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      ScrollLock.unlock();
      Overlay.hide(this);
      if (this.releaseTrap) this.releaseTrap();
      const ms = reducedMotion.matches ? 0 : 460;
      window.setTimeout(() => { if (!this.isOpen) this.hidden = true; }, ms);
      if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
    }
    static closeAll(except) {
      $$('nav-drawer, filter-drawer, cart-drawer').forEach((d) => { if (d !== except && d.isOpen) d.close(); });
    }
  }
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    e.preventDefault();
    drawer.open(opener);
  });

  // Escape always closes the open drawer, even if focus has left it
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (Lightbox.isOpen) { if (!Lightbox.el.contains(document.activeElement)) Lightbox.close(); return; }
    const modal = document.querySelector('quick-add-modal');
    if (modal && !modal.hidden) { if (!modal.contains(document.activeElement)) modal.close(); return; }
    const open = $$('nav-drawer, filter-drawer, cart-drawer').find((d) => d.isOpen);
    if (open && !open.contains(document.activeElement)) open.close();
  });

  class NavDrawer extends BaseDrawer {}
  customElements.define('nav-drawer', NavDrawer);

  class FilterDrawer extends BaseDrawer {}
  customElements.define('filter-drawer', FilterDrawer);

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends BaseDrawer {
    constructor() {
      super();
      this.overlayVariant = 'cart';
      this.pending = false;
      this.addEventListener('click', (e) => {
        const change = e.target.closest('[data-qty-change]');
        const remove = e.target.closest('[data-cart-remove]');
        if (change) { e.preventDefault(); this.updateLine(Number(change.dataset.qtyChange), Number(change.dataset.qty)); }
        if (remove) { e.preventDefault(); this.updateLine(Number(remove.dataset.cartRemove), 0); }
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-qty-input]');
        if (!input) return;
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        this.updateLine(Number(input.dataset.qtyInput), qty);
      });
      // restore correct state when the page comes back from the back/forward cache
      window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh(); });
    }
    get sectionId() { return 'cart-drawer'; }

    setError(message) {
      const el = this.querySelector('[data-cart-error]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    async updateLine(line, quantity) {
      if (this.pending) return;
      this.pending = true;
      this.classList.add('is-updating');
      this.setAttribute('aria-busy', 'true');
      this.setError('');
      try {
        const res = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: [this.sectionId], sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
        this.renderSections(data.sections);
        announce(quantity === 0 ? 'Item removed from cart' : 'Cart updated');
      } catch (err) {
        this.setError(err.message || strings.cartError);
        // re-sync with the server so the drawer never shows stale quantities
        this.refresh(true);
      } finally {
        this.pending = false;
        this.classList.remove('is-updating');
        this.removeAttribute('aria-busy');
      }
    }

    async refresh(keepError = false) {
      try {
        const res = await fetch(`${routes.root}?sections=${this.sectionId}`);
        const data = await res.json();
        const err = keepError ? (this.querySelector('[data-cart-error]') || {}).textContent : '';
        this.renderSections(data);
        if (err) this.setError(err);
      } catch (e) { /* offline: keep current state */ }
    }

    renderSections(sections) {
      if (!sections || !sections[this.sectionId]) return;
      const doc = parseHTML(sections[this.sectionId]);
      const fresh = doc.querySelector('cart-drawer');
      if (!fresh) return;
      const inner = this.querySelector('[data-cart-inner]');
      const scroller = this.querySelector('[data-cart-body]');
      const scrollTop = scroller ? scroller.scrollTop : 0;
      const activeId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.getAttribute('aria-label') : null;
      inner.innerHTML = fresh.querySelector('[data-cart-inner]').innerHTML;
      const newScroller = this.querySelector('[data-cart-body]');
      if (newScroller) newScroller.scrollTop = scrollTop;
      const count = Number(fresh.dataset.itemCount || 0);
      this.dataset.itemCount = String(count);
      $$('[data-cart-count]').forEach((b) => { b.textContent = count; b.classList.toggle('is-empty', count === 0); });
      // keep keyboard focus in the drawer after re-render
      if (this.isOpen) {
        const again = activeId && $$('[aria-label]', this).find((el) => el.getAttribute('aria-label') === activeId);
        (again || this.querySelector('[data-drawer-close]')).focus({ preventScroll: true });
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);
  const getCart = () => document.querySelector('cart-drawer');

  /* ==========================================================================
     Header
     ========================================================================== */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sticky !== 'true') return;
      this.wrapper = this.querySelector('[data-header-wrapper]');
      this.stuck = false;
      this.onScroll = this.onScroll.bind(this);
      this.measure = this.measure.bind(this);
      this.measure();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', debounce(this.measure, 150));
    }
    measure() {
      if (this.stuck) return;
      const rect = this.getBoundingClientRect();
      this.threshold = rect.top + window.scrollY + rect.height;
    }
    onScroll() {
      const y = window.scrollY;
      if (!this.stuck && y > this.threshold) {
        this.measure();
        // hold the header's space so content doesn't jump when it becomes fixed
        this.style.minHeight = `${this.getBoundingClientRect().height}px`;
        this.stuck = true;
        this.classList.add('is-stuck');
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-opening')));
      } else if (this.stuck && y <= this.threshold - this.offsetHeight) {
        this.stuck = false;
        this.classList.remove('is-stuck', 'is-opening');
        this.style.minHeight = '';
      }
    }
  }
  customElements.define('sticky-header', StickyHeader);

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.announcement-bar__slide', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      this.paused = false;
      const delay = Number(this.dataset.autoplay) || 5000;
      this.addEventListener('mouseenter', () => { this.paused = true; });
      this.addEventListener('mouseleave', () => { this.paused = false; });
      this.addEventListener('focusin', () => { this.paused = true; });
      this.addEventListener('focusout', () => { this.paused = false; });
      this.timer = window.setInterval(() => { if (!this.paused && !document.hidden) this.next(); }, delay);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      const current = this.slides[this.index];
      this.index = (this.index + 1) % this.slides.length;
      const next = this.slides[this.index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
      $$('a', current).forEach((a) => a.setAttribute('tabindex', '-1'));
      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
      $$('a', next).forEach((a) => a.removeAttribute('tabindex'));
      window.setTimeout(() => current.classList.remove('is-leaving'), 520);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.hero__slide', this);
      if (this.slides.length < 2 || reducedMotion.matches) return;
      this.index = 0;
      this.timer = window.setInterval(() => { if (!document.hidden && !this.matches(':hover')) this.go(this.index + 1); }, Number(this.dataset.autoplay) || 6000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    go(i) {
      this.slides[this.index].classList.remove('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
      this.slides[this.index].removeAttribute('aria-hidden');
    }
  }
  customElements.define('hero-slideshow', HeroSlideshow);

  /* ---------- Predictive search (Section Rendering API) ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.cache = new Map();
      this.openers = $$('[data-search-open]');
      this.openers.forEach((btn) => btn.addEventListener('click', (e) => { e.preventDefault(); this.open(btn); }));
      this.querySelector('[data-search-close]').addEventListener('click', () => this.close());
      this.input.addEventListener('input', debounce(() => this.search(), 250));
      this.onDocClick = (e) => { if (this.isOpen && !this.contains(e.target) && !e.target.closest('[data-search-open]')) this.close(); };
      document.addEventListener('click', this.onDocClick);
    }
    open(opener) {
      if (this.isOpen) return;
      BaseDrawer.closeAll();
      this.isOpen = true;
      this.opener = opener;
      this.hidden = false;
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      Overlay.show(this, 'modal');
      this.releaseTrap = trapFocus(this, () => this.close());
      requestAnimationFrame(() => this.input.focus());
    }
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.hidden = true;
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      Overlay.hide(this);
      if (this.releaseTrap) this.releaseTrap();
      if (this.opener) this.opener.focus({ preventScroll: true });
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.hidden = true; this.input.setAttribute('aria-expanded', 'false'); return; }
      if (this.controller) this.controller.abort();
      if (this.cache.has(q)) { this.render(this.cache.get(q)); return; }
      this.controller = new AbortController();
      this.results.classList.add('is-loading');
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,query&resources[limit]=4&resources[options][unavailable_products]=last&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const inner = parseHTML(html).querySelector('[data-predictive-inner]');
        const markup = inner ? inner.outerHTML : '';
        this.cache.set(q, markup);
        this.render(markup);
      } catch (e) {
        if (e.name !== 'AbortError') { this.results.hidden = true; }
      } finally {
        this.results.classList.remove('is-loading');
      }
    }
    render(markup) {
      this.results.innerHTML = markup;
      this.results.hidden = !markup;
      this.input.setAttribute('aria-expanded', String(!!markup));
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ==========================================================================
     Product
     ========================================================================== */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.sectionId = this.dataset.sectionId;
      this.picker = this.querySelector('variant-picker');
      this.form = this.querySelector('product-form');
      this.gallery = this.querySelector('product-gallery');
      this.sticky = this.querySelector('sticky-atc');
      if (this.picker) this.picker.addEventListener('change', () => this.onOptionChange());
      this.currentVariant = this.findVariant(this.selectedOptions()) || null;
      this.updateAvailability();
      if (this.currentVariant) this.updateInventory(this.currentVariant);
      this.setupSizeChart();
    }
    selectedOptions() {
      if (!this.picker) return this.product.variants[0].options;
      return $$('fieldset', this.picker).map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }
    findVariant(options) {
      return this.product.variants.find((v) => v.options.every((o, i) => o === options[i]));
    }
    setOption(index, value) {
      if (!this.picker) return;
      const fs = $$('fieldset', this.picker)[index];
      const input = fs && $$('input', fs).find((i) => i.value === value);
      if (input && !input.checked) { input.checked = true; this.onOptionChange(); }
    }
    onOptionChange() {
      const options = this.selectedOptions();
      const variant = this.findVariant(options);
      this.currentVariant = variant || null;
      $$('fieldset', this.picker).forEach((fs, i) => {
        const v = fs.querySelector('[data-option-value]');
        if (v) v.textContent = options[i] || '';
      });
      this.updateAvailability();
      this.updateForm(variant);
      this.updatePrice(variant);
      this.updateInventory(variant);
      this.updateSticky(variant, options);
      if (variant) {
        if (variant.featured_media_id && this.gallery) this.gallery.showMedia(variant.featured_media_id);
        if (this.dataset.updateUrl === 'true') {
          const url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
        }
      }
    }
    /* Option values unavailable for the current selection get a strike-through (reference). */
    updateAvailability() {
      if (!this.picker) return;
      const selected = this.selectedOptions();
      $$('fieldset', this.picker).forEach((fs, idx) => {
        $$('input', fs).forEach((input) => {
          const candidates = this.product.variants.filter((v) =>
            v.options[idx] === input.value && v.options.every((o, j) => j >= idx || o === selected[j])
          );
          const available = candidates.some((v) => v.available);
          const exists = candidates.length > 0;
          const label = fs.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('disabled', !available);
          input.classList.toggle('is-unavailable', !available);
          input.dataset.exists = String(exists);
        });
      });
    }
    updateForm(variant) {
      if (!this.form) return;
      const idInput = this.form.querySelector('[data-variant-id-input]');
      const btn = this.form.querySelector('[data-add-to-cart]');
      const txt = this.form.querySelector('[data-add-to-cart-text]');
      this.form.setError('');
      if (!variant) {
        btn.disabled = true;
        txt.textContent = strings.unavailable;
        return;
      }
      idInput.value = variant.id;
      btn.disabled = !variant.available;
      txt.textContent = variant.available ? strings.addToCart : strings.soldOut;
    }
    updatePrice(variant) {
      const wrap = this.querySelector('[data-price-wrap]');
      if (!wrap) return;
      const priceEl = wrap.querySelector('[data-price]');
      const compareEl = wrap.querySelector('[data-compare-price]');
      const saveEl = wrap.querySelector('[data-savings]');
      const onSale = variant && variant.compare_at_price > variant.price;
      wrap.style.visibility = variant ? '' : 'hidden';
      if (!variant) return;
      priceEl.textContent = formatMoney(variant.price);
      priceEl.classList.toggle('on-sale', onSale);
      compareEl.hidden = !onSale;
      saveEl.hidden = !onSale;
      wrap.querySelector('[data-compare-label]').hidden = !onSale;
      wrap.querySelector('[data-sale-label]').hidden = !onSale;
      if (onSale) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
        saveEl.textContent = strings.save.replace('[percent]', pct);
      }
    }
    /* Real inventory only — never invented scarcity. */
    updateInventory(variant) {
      const block = this.querySelector('.product-block--inventory');
      if (!block) return;
      const p = block.querySelector('[data-inventory]');
      const t = block.querySelector('[data-inventory-text]');
      const threshold = Number((theme.settings || {}).lowStockThreshold || 0);
      let text = '';
      let low = false;
      if (!variant) text = strings.unavailable;
      else if (!variant.available) text = strings.soldOut;
      else if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold) {
        text = strings.onlyLeft.replace('[count]', variant.inventory_quantity);
        low = true;
      } else if (block.dataset.showInStock === 'true') text = strings.inStock;
      t.textContent = text;
      p.hidden = !text;
      p.classList.toggle('is-low', low);
      p.classList.toggle('is-out', !variant || !variant.available);
    }
    updateSticky(variant, options) {
      if (!this.sticky) return;
      $$('[data-sticky-option]', this.sticky).forEach((sel) => { sel.value = options[Number(sel.dataset.stickyOption)]; });
      const price = this.sticky.querySelector('[data-sticky-price]');
      const compare = this.sticky.querySelector('[data-sticky-compare]');
      const btn = this.sticky.querySelector('[data-sticky-add]');
      const img = this.sticky.querySelector('[data-sticky-img]');
      if (variant) {
        price.textContent = formatMoney(variant.price);
        compare.hidden = !(variant.compare_at_price > variant.price);
        if (!compare.hidden) compare.textContent = formatMoney(variant.compare_at_price);
        if (img && variant.featured_image) { img.src = variant.featured_image; img.removeAttribute('srcset'); }
      }
      btn.disabled = !variant || !variant.available;
      btn.textContent = !variant ? strings.unavailable : (variant.available ? strings.addToCart : strings.soldOut);
    }
    setupSizeChart() {
      const dialog = this.querySelector('dialog.size-chart');
      if (!dialog) return;
      this.addEventListener('click', (e) => {
        const openBtn = e.target.closest('[data-size-chart-open]');
        if (openBtn) { e.preventDefault(); this.sizeChartOpener = openBtn; dialog.showModal(); }
      });
      dialog.addEventListener('click', (e) => {
        if (e.target.closest('[data-size-chart-close]') || e.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => { if (this.sizeChartOpener) this.sizeChartOpener.focus(); });
    }
  }
  customElements.define('product-info', ProductInfo);

  class VariantPicker extends HTMLElement {}
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Product form → /cart/add.js ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.formEl = this.querySelector('form');
      this.button = this.querySelector('[data-add-to-cart]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.pending = false;
      if (this.formEl) this.formEl.addEventListener('submit', (e) => this.onSubmit(e));
    }
    setError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg || '';
      this.errorEl.hidden = !msg;
    }
    async onSubmit(e) {
      e.preventDefault();
      if (this.pending || this.button.disabled) return; // blocks duplicate clicks while loading
      const cart = getCart();
      if (!cart) { this.formEl.submit(); return; }
      this.pending = true;
      this.setError('');
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      this.button.setAttribute('aria-disabled', 'true');
      const body = new FormData(this.formEl);
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
        cart.renderSections(data.sections);
        announce(`${data.product_title || 'Item'} added to cart`);
        const modal = this.closest('quick-add-modal');
        if (modal) modal.close({ returnFocus: false });
        if ((theme.settings || {}).cartOpenOnAdd !== false) cart.open(document.querySelector('[data-drawer-open="CartDrawer"]'));
        else { this.button.classList.add('is-added'); window.setTimeout(() => this.button.classList.remove('is-added'), 1500); }
      } catch (err) {
        this.setError(err.message || strings.cartError);
      } finally {
        this.pending = false;
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-busy');
        this.button.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Gallery: thumbs (desktop), swipe + dots (mobile), zoom lightbox ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = $$('.product__slide', this.track);
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.addEventListener('click', (e) => {
        const go = e.target.closest('[data-go]');
        const zoom = e.target.closest('[data-zoom]');
        const zoomActive = e.target.closest('[data-zoom-active]');
        if (go) { e.preventDefault(); this.goTo(Number(go.dataset.go)); }
        else if (zoomActive) { e.preventDefault(); Lightbox.open(this.zoomSources(), this.index, zoomActive); }
        else if (zoom && !mqSmall.matches) { e.preventDefault(); Lightbox.open(this.zoomSources(), Number(zoom.dataset.zoomIndex), zoom); }
      });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
      });
      this.track.addEventListener('scroll', debounce(() => this.syncFromScroll(), 60), { passive: true });
      if (mqSmall.matches && this.index > 0) {
        requestAnimationFrame(() => { this.track.scrollLeft = this.slides[this.index].offsetLeft; });
      }
    }
    zoomSources() { return $$('[data-zoom]', this).map((b) => b.dataset.zoom); }
    showMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.goTo(i);
    }
    goTo(i) {
      if (!this.slides.length) return;
      i = (i + this.slides.length) % this.slides.length;
      if (mqSmall.matches) {
        this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
      this.setActive(i);
    }
    syncFromScroll() {
      if (!mqSmall.matches) return;
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (i !== this.index) this.setActive(i);
    }
    setActive(i) {
      this.index = i;
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
      $$('.product__thumb', this).forEach((t, n) => {
        t.classList.toggle('is-active', n === i);
        if (n === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
      $$('.product__dot', this).forEach((d, n) => {
        d.classList.toggle('is-active', n === i);
        d.setAttribute('aria-selected', String(n === i));
      });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  const Lightbox = {
    get el() { return document.getElementById('Lightbox'); },
    open(sources, index, opener) {
      const el = this.el;
      if (!el || !sources.length) return;
      this.sources = sources; this.opener = opener; this.index = index || 0;
      el.hidden = false;
      requestAnimationFrame(() => el.classList.add('is-open'));
      ScrollLock.lock();
      this.show();
      el.querySelector('[data-lightbox-prev]').hidden = sources.length < 2;
      el.querySelector('[data-lightbox-next]').hidden = sources.length < 2;
      if (!this.bound) {
        this.bound = true;
        el.addEventListener('click', (e) => {
          if (e.target.closest('[data-lightbox-close]') || e.target === el) this.close();
          if (e.target.closest('[data-lightbox-prev]')) { this.index--; this.show(); }
          if (e.target.closest('[data-lightbox-next]')) { this.index++; this.show(); }
        });
        el.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') { this.index--; this.show(); }
          if (e.key === 'ArrowRight') { this.index++; this.show(); }
        });
        let startX = null;
        el.addEventListener('touchstart', (e) => { startX = e.touches.length === 1 ? e.touches[0].clientX : null; }, { passive: true });
        el.addEventListener('touchend', (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 50) { this.index += dx < 0 ? 1 : -1; this.show(); }
          startX = null;
        });
      }
      this.release = trapFocus(el, () => this.close());
      this.isOpen = true;
      window.setTimeout(() => el.querySelector('[data-lightbox-close]').focus(), 40);
    },
    show() {
      const n = this.sources.length;
      this.index = (this.index + n) % n;
      this.el.querySelector('[data-lightbox-img]').src = this.sources[this.index];
    },
    close() {
      const el = this.el;
      if (!this.isOpen) return;
      this.isOpen = false;
      el.classList.remove('is-open');
      ScrollLock.unlock();
      if (this.release) this.release();
      window.setTimeout(() => { el.hidden = true; }, reducedMotion.matches ? 0 : 260);
      if (this.opener) this.opener.focus({ preventScroll: true });
    }
  };

  /* ---------- Sticky add-to-cart (appears once the main button scrolls above the viewport) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.info = this.closest('product-info');
      const target = this.info && this.info.querySelector('[data-main-atc]');
      if (!target) return;
      // rAF-throttled scroll check (an IntersectionObserver can miss a fast fling past the button)
      let ticking = false;
      this.visible = false;
      this.check = () => {
        ticking = false;
        const passed = target.getBoundingClientRect().bottom < 0;
        if (passed === this.visible) return;
        this.visible = passed;
        this.classList.toggle('is-visible', passed);
        this.setAttribute('aria-hidden', String(!passed));
        $$('select, button', this).forEach((el) => { el.tabIndex = passed ? 0 : -1; });
      };
      this.onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(this.check); } };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.check();
      this.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-sticky-option]');
        if (sel) this.info.setOption(Number(sel.dataset.stickyOption), sel.value);
      });
      this.querySelector('[data-sticky-add]').addEventListener('click', () => {
        const form = this.info.querySelector('product-form form');
        if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Quick add modal ---------- */
  class QuickAddModal extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-modal-content]');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-modal-close]') || e.target === this) this.close();
      });
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn) return;
        e.preventDefault();
        this.load(btn);
      });
    }
    async load(btn) {
      if (this.loading) return;
      this.loading = true;
      btn.classList.add('is-loading');
      this.open(btn);
      this.content.classList.add('is-loading');
      this.content.innerHTML = '';
      try {
        const url = new URL(btn.dataset.quickAdd, window.location.origin);
        url.searchParams.set('section_id', 'quick-add');
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const el = doc.querySelector('.quick-add');
        if (!el) throw new Error('missing');
        this.content.innerHTML = el.outerHTML;
        const title = this.content.querySelector('.quick-add__title');
        if (title) this.setAttribute('aria-label', title.textContent.trim());
        const first = this.content.querySelector('input:checked, [data-add-to-cart]');
        if (first) first.focus({ preventScroll: true });
      } catch (e) {
        // Fall back to the product page if the section can't be loaded
        window.location.href = btn.dataset.quickAdd;
      } finally {
        this.content.classList.remove('is-loading');
        btn.classList.remove('is-loading');
        this.loading = false;
      }
    }
    open(opener) {
      this.opener = opener;
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      ScrollLock.lock();
      this.release = trapFocus(this, () => this.close());
      window.setTimeout(() => this.querySelector('[data-modal-close]').focus({ preventScroll: true }), 40);
    }
    close({ returnFocus = true } = {}) {
      if (this.hidden) return;
      this.classList.remove('is-open');
      ScrollLock.unlock();
      if (this.release) this.release();
      window.setTimeout(() => { this.hidden = true; this.content.innerHTML = ''; }, reducedMotion.matches ? 0 : 300);
      if (returnFocus && this.opener) this.opener.focus({ preventScroll: true });
    }
  }
  customElements.define('quick-add-modal', QuickAddModal);

  /* ---------- Simple scroll carousel (testimonials) ---------- */
  class CarouselSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.controls = this.querySelector('.carousel-controls');
      const step = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      this.prev.addEventListener('click', () => step(-1));
      this.next.addEventListener('click', () => step(1));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        this.controls.hidden = max <= 0;
        this.prev.disabled = this.track.scrollLeft <= 2;
        this.next.disabled = this.track.scrollLeft >= max;
      };
      this.track.addEventListener('scroll', debounce(this.update, 50), { passive: true });
      window.addEventListener('resize', debounce(this.update, 150));
      this.update();
    }
  }
  customElements.define('carousel-slider', CarouselSlider);

  /* ==========================================================================
     Collection filtering & sorting (Section Rendering API + history)
     ========================================================================== */
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.bind();
      this.onPopState = (e) => {
        if (e.state && e.state.collectionFilters) this.fetch(window.location.href, false);
      };
      window.addEventListener('popstate', this.onPopState);
      window.history.replaceState({ collectionFilters: true }, '', window.location.href);
    }
    disconnectedCallback() { window.removeEventListener('popstate', this.onPopState); }
    bind() {
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-sort]')) { this.applyForm(); return; }
        if (e.target.closest('[data-filter-form]')) {
          if (e.target.type === 'number') this.debouncedApply(); else this.applyForm();
        }
      });
      this.debouncedApply = debounce(() => this.applyForm(), 600);
      this.addEventListener('input', (e) => {
        if (e.target.type === 'number' && e.target.closest('[data-filter-form]')) this.debouncedApply();
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-filter-link]');
        if (!link) return;
        e.preventDefault();
        const isPage = link.closest('.pagination');
        this.fetch(link.href, true, isPage);
      });
      const form = this.querySelector('[data-filter-form]');
      if (form) form.addEventListener('submit', (e) => { e.preventDefault(); this.applyForm(); });
    }
    applyForm() {
      const form = this.querySelector('[data-filter-form]');
      const sort = this.querySelector('[data-sort]');
      const params = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams();
      if (sort) params.set('sort_by', sort.value);
      // drop empty price inputs
      for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
      const base = form ? new URL(form.getAttribute('action'), window.location.origin).pathname : window.location.pathname;
      const url = `${base}?${params.toString()}`;
      this.fetch(url, true);
    }
    async fetch(href, push = true, scrollToTop = false) {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('section_id');
      const renderUrl = new URL(url.toString());
      renderUrl.searchParams.set('section_id', this.sectionId);
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const results = this.querySelector('[data-collection-results]');
      results.classList.add('is-loading');
      results.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(renderUrl.toString(), { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const fresh = doc.querySelector('collection-filters');
        if (!fresh) throw new Error('missing');
        this.swap(fresh);
        if (push) window.history.pushState({ collectionFilters: true }, '', url.toString());
        const count = this.querySelector('[data-product-count]');
        if (count) announce(count.textContent.trim());
        if (scrollToTop) {
          const top = this.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        }
      } catch (e) {
        if (e.name !== 'AbortError') window.location.href = url.toString();
      } finally {
        results.classList.remove('is-loading');
        results.removeAttribute('aria-busy');
      }
    }
    swap(fresh) {
      const replace = (sel) => {
        const a = this.querySelector(sel);
        const b = fresh.querySelector(sel);
        if (a && b) a.replaceWith(b);
        else if (a && !b) a.remove();
        else if (!a && b) {
          // active filter row appears for the first time: insert after toolbar
          const toolbar = this.querySelector('[data-toolbar]');
          if (toolbar) toolbar.insertAdjacentElement('afterend', b);
        }
      };
      replace('[data-collection-results]');
      replace('[data-active-filters]');
      const count = this.querySelector('[data-product-count]');
      const newCount = fresh.querySelector('[data-product-count]');
      if (count && newCount) count.textContent = newCount.textContent;
      const badge = this.querySelector('[data-active-count]');
      const newBadge = fresh.querySelector('[data-active-count]');
      if (badge && newBadge) { badge.textContent = newBadge.textContent; badge.className = newBadge.className; }
      const sort = this.querySelector('[data-sort]');
      const newSort = fresh.querySelector('[data-sort]');
      if (sort && newSort) sort.value = newSort.value;
      // filter groups: keep which groups are open
      const groups = this.querySelector('[data-filter-groups]');
      const newGroups = fresh.querySelector('[data-filter-groups]');
      if (groups && newGroups) {
        const open = $$('[data-collapsible-trigger][aria-expanded="true"]', groups).map((t) => t.getAttribute('aria-controls'));
        const focusedName = document.activeElement && groups.contains(document.activeElement) ? `${document.activeElement.name}|${document.activeElement.value}` : null;
        groups.innerHTML = newGroups.innerHTML;
        $$('[data-collapsible-trigger]', groups).forEach((t) => {
          if (open.includes(t.getAttribute('aria-controls'))) setCollapsible(t, true, false);
        });
        if (focusedName) {
          const [n, v] = focusedName.split('|');
          const el = $$('input', groups).find((i) => i.name === n && (i.type === 'number' || i.value === v));
          if (el) {
            el.focus({ preventScroll: true });
          } else {
            const drawer = this.querySelector('filter-drawer');
            if (drawer && drawer.isOpen) drawer.querySelector('[data-drawer-close]').focus({ preventScroll: true });
          }
        }
      }
      const inline = this.querySelector('[data-product-count-inline]');
      const newInline = fresh.querySelector('[data-product-count-inline]');
      if (inline && newInline) inline.textContent = newInline.textContent;
      const sortMirror = this.querySelector('[data-sort-mirror]');
      if (sortMirror && sort) sortMirror.value = sort.value;
    }
  }
  customElements.define('collection-filters', CollectionFilters);

  /* ---------- Product recommendations (optional section) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const doc = parseHTML(await res.text());
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        } catch (e) { /* ignore */ }
      };
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) { io.disconnect(); load(); }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Init ---------- */
  initCollapsibles();
  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => initCollapsibles(e.target));
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector('cart-drawer')) getCart().open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    if (e.target.querySelector('cart-drawer')) getCart().close();
  });

  window.MeridianTheme = { formatMoney, getCart, initCollapsibles };
})();
